import { OrganizationTheme } from '../../models/theme.interface';

/**
 * BeInspired Theme Configuration
 *
 * This theme uses a teal/turquoise color scheme (#00B098)
 * as the primary brand color, different from DoenKids' pink.
 */
export const beinspiredTheme: OrganizationTheme = {
  id: 'beinspired',
  name: 'BeInspired',
  brandName: 'BE Inspired',

  // Domains that should use this theme
  domains: [
    'app.beinspired.com',
    'beinspired.com',
    'blossombeinspired-admin-test.firebaseapp.com',
    'blossombeinspired-admin-test.web.app',
    'online.blossombeinspired.co.uk',
  ],

  colors: {
    // Primary brand color (teal/turquoise)
    primary: {
      main: '#00B098',
      light: '#b3e7e0',    // $mainColor-100
      medium: '#4dc8b7',   // $mainColor-400
      dark: '#00876a',     // $mainColor-600
      contrast: '#ffffff',
    },

    // Secondary color (pink/magenta - different from DoenKids primary)
    secondary: {
      main: '#e50081',     // BeInspired secondary main
      light: '#f7b3d9',    // pink-100
      medium: '#e92694',   // pink-400
      dark: '#de006e',     // pink-700
      contrast: '#ffffff',
    },

    // Warning/error color (red) - same as DoenKids
    warn: {
      main: '#F44336',     // Material red-500
      light: '#FFCDD2',    // red-100
      medium: '#EF5350',   // red-400
      dark: '#D32F2F',     // red-700
      contrast: '#ffffff',
    },

    // Semantic colors
    success: '#2E7D32',    // Green
    warning: '#eb7c02',    // Orange (status2BgColor)
    error: '#C62828',      // Red (alert)
    info: '#536dfe',       // Blue
    purple: '#7B1FA2',     // Purple for special cases

    // Neutral colors
    white: '#ffffff',
    grey: {
      100: '#f5f5f5',      // Light grey (used for backgrounds)
      300: '#e0e0e0',      // Medium light grey
      400: '#bdbdbd',      // Medium grey
      500: '#9e9e9e',      // Medium dark grey
      700: '#616161',      // Dark grey
      900: '#424242',      // Very dark grey
    },
    black: '#000',
    lightgrey: '#e0e0e0',
    whitesmoke: '#f5f5f5',

    // Background colors
    cardBackground: '#ffffff',  // White for BeInspired cards
    organizationUnitSelectorBackground: '#00876a',  // Primary dark (teal)

    // Text colors
    text: {
      main: '#212121',          // Dark text
      subdued: '#757575',       // Muted text
      onPrimary: '#ffffff',     // White text on teal
      onSecondary: '#ffffff',   // White text on pink
      onWarn: '#ffffff',        // White text on red
    },

    // Transparent overlays (same as DoenKids)
    transparent: {
      0: '#00000000',
      50: '#0000000d',
      120: '#0000001f',
      140: '#00000024',
      200: '#00000033',
      300: '#0000004d',
      400: '#00000066',
      500: 'rgba(0, 0, 0, 0.5)',
      600: '#00000099',
      700: 'rgba(0, 0, 0, 0.7)',
      900: '#000000e6',
    },

    // Context-specific colors
    alert: '#C62828',
    alertLight: '#ecb9b9',
    attachmentLink: '#474747',

    // Box shadows (for special effects)
    videoBoxShadow: '0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)',

    // Status colors (for publication workflow)
    status: {
      1: { bg: '#ffcc00' },                      // $status1BgColor
      2: { bg: '#eb7c02' },                      // $status2BgColor
      3: { bg: '#83bb26' },                      // $status3BgColor
      4: { bg: '#c0c0c0', color: '#a5a5a5' },   // $status4BgColor, $status4Color
      5: { bg: '#ECEDDC', color: '#ECEDDC' },   // $status5Color
    },

    // Organization type colors
    bso: '#00bcd4',        // BSO (buitenschoolse opvang) color
    kdv: '#e91e63',        // KDV (kinderopvang) color

    // Message colors (different from DoenKids)
    message: {
      subtitle: '#F663A0',     // $messageSubtitle (BeInspired-specific pink)
      paragraph: '#777777',    // $messageParagraph
    },
  },

  fonts: {
    // Global fonts
    primary: 'Figtree, Roboto, "Helvetica Neue", sans-serif',
    secondary: 'Figtree, Roboto, "Helvetica Neue", sans-serif',
    logo: 'Moranga, sans-serif',

    // Area-specific fonts
    sidebar: 'Figtree, Roboto, "Helvetica Neue", sans-serif',
    content: 'Figtree, Roboto, "Helvetica Neue", sans-serif',

    // Font sizes
    sizes: {
      xs: '12px',
      sm: '14px',
      base: '16px',
      md: '18px',
      lg: '20px',
      xl: '24px',
    },

    // Font weights
    weights: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },

  ui: {
    borderRadius: {
      none: '0',
      sm: '4px',
      md: '8px',
      lg: '16px',
      xl: '24px',
      full: '9999px',
    },

    spacing: {
      unit: '8px',
    },

    rimWidth: '24px',  // Width of the app rim/border

    shadows: {
      light: '0 2px 4px rgba(0, 0, 0, 0.1)',
      medium: '0 4px 8px rgba(0, 0, 0, 0.15)',
      heavy: '0 8px 16px rgba(0, 0, 0, 0.2)',
    },
  },

  assets: {
    logo: './assets/images/logo-be-inspired.png',
    favicon: 'assets/favicons/favicon-beinspired.ico',
    poweredByLogo: './assets/images/logo-be-inspired.png',
    sidebarBackground: './assets/images/background-pattern.jpeg',
    avatarImg: './assets/images/avatar-beinspired.png',
    activityPlaceholder: './assets/images/activities/activity-placeholder-beinspired.png',
    activityBlockedImage: './assets/images/activities/activity-blocked.png',
    activity404Image: './assets/images/activities/activity-404-beinspired.png',
  },

  auth0: {
    logoUrl: './assets/images/logo-be-inspired.png',
    themeColor: '#00B098',
  },
};
